// The stage is the roll moment on the board: the die tumbles, lands, and
// the spell card shows. The board and the cue bar hold their next state
// until the stage clears, so the reveal happens in one place. At the end of
// the match the stage holds the final card.

import { DIE_SPIN_MS, STAGE_ENERGY_HOLD_MS, STAGE_LEAVE_MS, STAGE_REVEAL_MS } from '../constants';
import type { RollRecord } from '../game/state';
import { createStore } from '../lib/store';
import { clearDelivery, startDelivery } from './energyDelivery';
import { gameStore } from './game';

export type StagePhase = 'hidden' | 'spinning' | 'landed' | 'leaving' | 'over';

export type StageState = {
  phase: StagePhase;
  roll: RollRecord | null;
};

export const stageStore = createStore<StageState>({ phase: 'hidden', roll: null });

export const stageIsShowing = (): boolean => stageStore.get().phase !== 'hidden';

let seenSerial = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
/** The player put the final card away, to look at the board. A new match brings the stage back. */
let finalCardPutAway = false;

const schedule = (ms: number, next: () => void): void => {
  if (timer) clearTimeout(timer);
  timer = setTimeout(next, ms);
};

/**
 * How long the die stays after it lands. A roll with a card stays for the
 * read. A roll that gave energy stays for as long as its coins fly to the
 * pips. A roll that gave nothing has only a face to read.
 */
const landedHoldMs = (roll: RollRecord): number => {
  if (roll.spellId !== null) return STAGE_REVEAL_MS;
  return roll.gain !== null && roll.gain > 0 ? startDelivery(roll) : STAGE_ENERGY_HOLD_MS;
};

/** Marks a roll as shown, so a match that loads from a save does not roll it again. */
export const markRollShown = (roll: RollRecord | null): void => {
  seenSerial = roll?.serial ?? 0;
};

/** Hides the final card for the rest of this match. The cue keeps the result. */
export const putAwayFinalCard = (): void => {
  if (stageStore.get().phase !== 'over') return;
  finalCardPutAway = true;
  if (timer) clearTimeout(timer);
  stageStore.set({ phase: 'hidden', roll: stageStore.get().roll });
};

/** Call once at startup. */
export const startStage = (): void => {
  gameStore.subscribe((state) => {
    const roll = state.lastRoll;
    // A side can give up before the first roll, so a match can be over with no roll.
    if (!roll && state.phase !== 'over') {
      seenSerial = 0;
      finalCardPutAway = false;
      if (timer) clearTimeout(timer);
      clearDelivery();
      stageStore.set({ phase: 'hidden', roll: null });
      return;
    }
    if (state.phase === 'over') {
      if (finalCardPutAway) return;
      // Let a roll that is mid-reveal finish, then hold the final card.
      if (stageStore.get().phase === 'hidden') stageStore.set({ phase: 'over', roll });
      else schedule(STAGE_REVEAL_MS, () => stageStore.set({ phase: 'over', roll }));
      return;
    }
    if (!roll || roll.serial === seenSerial) return;
    seenSerial = roll.serial;
    stageStore.set({ phase: 'spinning', roll });
    schedule(DIE_SPIN_MS, () => {
      // The delivery starts before the views hear of the landing, so no view shows the energy early.
      const holdMs = landedHoldMs(roll);
      stageStore.set({ phase: 'landed', roll });
      schedule(holdMs, () => {
        stageStore.set({ phase: 'leaving', roll });
        schedule(STAGE_LEAVE_MS, () => stageStore.set({ phase: 'hidden', roll }));
      });
    });
  });
};
